import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  Monitor,
  Trees,
  HeartHandshake,
  Sparkles,
  CalendarCheck,
  Zap,
  Search,
  SlidersHorizontal,
  ArrowRight,
  Smartphone,
} from "lucide-react";

// Each row below reuses the SAME catalog array its own full page renders
// from — never a hand-copied duplicate that can drift out of sync.
import { services as instantServicesList } from "./HomeServices";
import { services as caretakerServicesList } from "./CaretakerServices";
import { services as specialServicesList } from "./SpecialServices";
import { getCategoryHeroImage } from "../../config/serviceImages";

const sidebarCategories = [
  { id: "all", label: "All Categories", icon: Home, color: "#6b7280" },
  { id: "instant", label: "Instant Services", icon: Zap, color: "#4f46e5" },
  { id: "schedule", label: "Schedule Services", icon: CalendarCheck, color: "#16a34a" },
  { id: "caretaker", label: "Caretaker Services", icon: HeartHandshake, color: "#a855f7" },
  { id: "special", label: "Special Services", icon: Sparkles, color: "#c026d3" },
];

// Instant Services — 5 of HomeServices.js's individually-bookable,
// duration-sold items (not the smaller task-level add-ons folded into
// them). None of these carry their own `.link` — they're all reached at
// /service/home-service/instant/<id> (see InstantServiceDetailPage.js),
// same as every other hs-* entry in that catalog.
const INSTANT_PREVIEW_IDS = ["hs-1", "hs-2", "hs-4", "hs-9", "hs-10"];
const instantPreview = INSTANT_PREVIEW_IDS
  .map((id) => instantServicesList.find((s) => s.id === id))
  .filter(Boolean)
  .map((s) => ({ name: s.name, image: s.image, link: `/service/home-service/instant/${s.id}` }));

// Schedule Services — the Schedule Booking wizard's real category list
// (MongoDB config_schedule_booking, admin-editable via the Dashboard).
// Only 3 categories exist today — shown as-is, not padded with
// placeholders to match the other rows' counts.
const scheduleCategories = [
  { name: "Home Cleaning", slug: "home-cleaning", heroKey: "home_cleaning", icon: Home },
  { name: "Technical",     slug: "technical",     heroKey: "technical",     icon: Monitor },
  { name: "Outdoor",       slug: "outdoor",        heroKey: "outdoor",       icon: Trees },
].map((c) => ({ name: c.name, icon: c.icon, image: getCategoryHeroImage(c.heroKey), link: `/schedule-booking/${c.slug}` }));

// Caretaker Services — all 5 of CaretakerServices.js's own catalog.
const caretakerPreview = caretakerServicesList
  .slice(0, 5)
  .map((s) => ({ name: s.name, image: s.image, link: s.link }));

// Special Services — 5 of SpecialServices.js's 6 real services.
const specialPreview = specialServicesList
  .slice(0, 5)
  .map((s) => ({ name: s.name, subtitle: s.desc, image: s.image, link: s.link }));

const serviceSections = [
  {
    id: "instant",
    title: "Instant Services",
    icon: Zap,
    color: "#4f46e5",
    viewAllLink: "/services/instant-home-services",
    items: instantPreview,
  },
  {
    id: "schedule",
    title: "Schedule Services",
    icon: CalendarCheck,
    color: "#16a34a",
    viewAllLink: "/schedule-booking",
    items: scheduleCategories,
  },
  {
    id: "caretaker",
    title: "Caretaker Services",
    icon: HeartHandshake,
    color: "#a855f7",
    viewAllLink: "/services/caretaker-services",
    items: caretakerPreview,
  },
  {
    id: "special",
    title: "Special Services",
    icon: Sparkles,
    color: "#c026d3",
    viewAllLink: "/services/special-services",
    items: specialPreview,
  },
];

const AllServicesPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");

  const filteredSections = useMemo(() => {
	const sections =
	  activeCategory === "all"
		? serviceSections
		: serviceSections.filter((section) => section.id === activeCategory);

	if (!search.trim()) return sections;

	return sections
	  .map((section) => ({
		...section,
		items: section.items.filter((item) =>
		  item.name.toLowerCase().includes(search.toLowerCase())
		),
	  }))
	  .filter((section) => section.items.length > 0);
  }, [activeCategory, search]);

  return (
	<main style={styles.page}>
	  <div style={styles.wrapper}>
		<aside style={styles.sidebar}>
		  <div style={styles.sidebarTop}>
			{sidebarCategories.map((item) => {
			  const Icon = item.icon;
			  const isActive = activeCategory === item.id;

			  return (
				<button
				  key={item.id}
				  onClick={() => setActiveCategory(item.id)}
				  style={{
					...styles.sidebarItem,
					...(isActive ? styles.sidebarItemActive : {}),
				  }}
				>
				  <div
					style={{
					  ...styles.sidebarIconWrap,
					  background: isActive ? "#eef2ff" : "transparent",
					}}
				  >
					<Icon size={18} color={item.color} />
				  </div>
				  <span
					style={{
					  ...styles.sidebarText,
					  color: isActive ? "#4f46e5" : "#374151",
					  fontWeight: isActive ? 700 : 500,
					}}
				  >
					{item.label}
				  </span>
				</button>
			  );
			})}
		  </div>

		  <div style={styles.appPromo}>
			<h3 style={styles.appPromoTitle}>Get the App for Faster Experience</h3>
			<p style={styles.appPromoText}>
			  Book, track and manage your services on the go.
			</p>

			<div style={styles.storeGroup}>
			  <button style={styles.storeBtn}>▶ Google Play</button>
			</div>

			<div style={styles.phoneMock}>
			  <div style={styles.phoneNotch} />
			  <div style={styles.phoneScreen}>
				<p style={styles.mockSmall}>Hello, Priya 👋</p>
				<h4 style={styles.mockHeading}>What service do you need today?</h4>
				<div style={styles.mockSearch}>Search for services...</div>
				<div style={styles.mockGrid}>
				  <span style={styles.mockBox}>🧹</span>
				  <span style={styles.mockBox}>🔧</span>
				  <span style={styles.mockBox}>❄️</span>
				  <span style={styles.mockBox}>⚡</span>
				</div>
			  </div>
			</div>
		  </div>
		</aside>

		<section style={styles.content}>
		  <div style={styles.topBar}>
			<div>
			  <h1 style={styles.pageTitle}>All Services</h1>
			  <p style={styles.pageSubTitle}>
				Explore our wide range of professional services
			  </p>
			</div>

			<div style={styles.topActions}>
			  <div style={styles.searchBar}>
				<Search size={16} color="#9ca3af" />
				<input
				  type="text"
				  placeholder="Search for services..."
				  value={search}
				  onChange={(e) => setSearch(e.target.value)}
				  style={styles.searchInput}
				/>
			  </div>

			  <button style={styles.filterBtn}>
				<SlidersHorizontal size={16} />
				Filter
			  </button>
			</div>
		  </div>

		  <div style={styles.sectionsWrap}>
			{filteredSections.map((section) => {
			  const Icon = section.icon;

			  return (
				<div key={section.id} style={styles.sectionCard}>
				  <div style={styles.sectionHeader}>
					<div style={styles.sectionTitleWrap}>
					  <div
						style={{
						  ...styles.sectionIconBadge,
						  background: `${section.color}14`,
						}}
					  >
						<Icon size={16} color={section.color} />
					  </div>
					  <h2 style={styles.sectionTitle}>{section.title}</h2>
					</div>

					{section.viewAllLink && (
					  <Link
						to={section.viewAllLink}
						style={{ textDecoration: "none" }}
					  >
						<button style={styles.viewAllBtn}>
						  View All <ArrowRight size={14} />
						</button>
					  </Link>
					)}
				  </div>

				  <div
					style={{
					  ...styles.serviceGrid,
					  // Fixed card width (not item-count-based) so a row with
					  // only 3 items (e.g. Schedule Services) doesn't stretch
					  // its cards much wider/taller than a 5-6 item row.
					  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
					  ...(section.id === "special" ? { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" } : {}),
					}}
				  >
					{section.items.map((item) => (
					  <Link
						key={item.name}
						to={item.link}
						style={{ textDecoration: "none", color: "inherit" }}
					  >
						<div
						  style={{
							...styles.serviceCard,
							...(section.id === "special"
							  ? styles.specialCard
							  : {}),
						  }}
						>
						  {item.image ? (
							<img
							  src={item.image}
							  alt={item.name}
							  style={{
								...styles.serviceImage,
								...(section.id === "special"
								  ? styles.specialImage
								  : {}),
							  }}
							/>
						  ) : (
							<div
							  style={{
								...styles.serviceImage,
								...(section.id === "special"
								  ? styles.specialImage
								  : {}),
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								background: `${section.color}14`,
							  }}
							>
							  {item.icon && <item.icon size={36} color={section.color} />}
							</div>
						  )}

						  <div
							style={{
							  ...styles.serviceBody,
							  ...(section.id === "special"
								? styles.specialBody
								: {}),
							}}
						  >
							<h3 style={styles.serviceName}>{item.name}</h3>
							{item.subtitle ? (
							  <p style={styles.serviceSub}>{item.subtitle}</p>
							) : null}
						  </div>
						</div>
					  </Link>
					))}
				  </div>
				</div>
			  );
			})}

			{filteredSections.length === 0 && (
			  <div style={styles.emptyState}>
				<Smartphone size={28} color="#6366f1" />
				<h3 style={styles.emptyTitle}>No services found</h3>
				<p style={styles.emptyText}>
				  Try another keyword or choose a different category.
				</p>
			  </div>
			)}
		  </div>
		</section>
	  </div>
	</main>
  );
};

const styles = {
  page: {
	background: "#f8fafc",
	minHeight: "100vh",
	padding: "20px",
	fontFamily:
	  "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
	color: "#111827",
  },
  wrapper: {
	display: "grid",
	gridTemplateColumns: "280px 1fr",
	gap: "20px",
	alignItems: "start",
  },
  sidebar: {
	display: "flex",
	flexDirection: "column",
	gap: "16px",
	position: "sticky",
	top: "20px",
  },
  sidebarTop: {
	background: "#fff",
	border: "1px solid #e5e7eb",
	borderRadius: "18px",
	padding: "14px",
  },
  sidebarItem: {
	width: "100%",
	display: "flex",
	alignItems: "center",
	gap: "12px",
	border: "none",
	background: "transparent",
	borderRadius: "14px",
	padding: "14px 12px",
	cursor: "pointer",
	textAlign: "left",
	marginBottom: "6px",
  },
  sidebarItemActive: {
	background: "#f5f7ff",
  },
  sidebarIconWrap: {
	width: "28px",
	height: "28px",
	borderRadius: "10px",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	flexShrink: 0,
  },
  sidebarText: {
	fontSize: "15px",
  },
  appPromo: {
	background: "#dfe4ff",
	borderRadius: "18px",
	padding: "22px 18px 0",
	overflow: "hidden",
	border: "1px solid rgba(79,70,229,0.08)",
  },
  appPromoTitle: {
	margin: 0,
	fontSize: "30px",
	lineHeight: 1.15,
	fontWeight: 800,
	color: "#1f2937",
	marginBottom: "12px",
  },
  appPromoText: {
	margin: 0,
	fontSize: "15px",
	lineHeight: 1.6,
	color: "#4b5563",
	marginBottom: "18px",
  },
  storeGroup: {
	display: "flex",
	flexDirection: "column",
	gap: "10px",
	marginBottom: "22px",
  },
  storeBtn: {
	background: "#111827",
	color: "#fff",
	border: "none",
	borderRadius: "12px",
	padding: "12px 14px",
	fontSize: "14px",
	fontWeight: 700,
	textAlign: "left",
	cursor: "pointer",
  },
  phoneMock: {
	width: "180px",
	height: "330px",
	margin: "0 auto",
	borderRadius: "28px",
	background: "#111827",
	padding: "8px",
	boxShadow: "0 16px 30px rgba(0,0,0,0.18)",
  },
  phoneNotch: {
	width: "86px",
	height: "16px",
	background: "#111827",
	borderRadius: "0 0 12px 12px",
	margin: "0 auto",
	position: "relative",
	top: "-1px",
	zIndex: 2,
  },
  phoneScreen: {
	background: "#fff",
	borderRadius: "22px",
	height: "100%",
	marginTop: "-9px",
	padding: "16px 12px",
  },
  mockSmall: {
	margin: "0 0 10px",
	fontSize: "11px",
	color: "#6b7280",
  },
  mockHeading: {
	margin: "0 0 10px",
	fontSize: "17px",
	lineHeight: 1.25,
	color: "#111827",
  },
  mockSearch: {
	background: "#f3f4f6",
	borderRadius: "10px",
	padding: "9px 10px",
	fontSize: "11px",
	color: "#9ca3af",
	marginBottom: "14px",
  },
  mockGrid: {
	display: "grid",
	gridTemplateColumns: "repeat(4, 1fr)",
	gap: "8px",
  },
  mockBox: {
	background: "#eef2ff",
	height: "40px",
	borderRadius: "10px",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	fontSize: "17px",
  },
  content: {
	minWidth: 0,
  },
  topBar: {
	display: "flex",
	alignItems: "flex-start",
	justifyContent: "space-between",
	gap: "20px",
	marginBottom: "18px",
  },
  pageTitle: {
	margin: 0,
	fontSize: "48px",
	lineHeight: 1.08,
	fontWeight: 800,
	color: "#111827",
	letterSpacing: "-1px",
	marginBottom: "8px",
  },
  pageSubTitle: {
	margin: 0,
	fontSize: "16px",
	color: "#6b7280",
	lineHeight: 1.6,
  },
  topActions: {
	display: "flex",
	alignItems: "center",
	gap: "12px",
	minWidth: "520px",
	justifyContent: "flex-end",
  },
  searchBar: {
	flex: 1,
	height: "52px",
	borderRadius: "14px",
	background: "#fff",
	border: "1px solid #e5e7eb",
	display: "flex",
	alignItems: "center",
	gap: "10px",
	padding: "0 16px",
  },
  searchInput: {
	flex: 1,
	border: "none",
	outline: "none",
	fontSize: "15px",
	color: "#111827",
	background: "transparent",
  },
  filterBtn: {
	height: "52px",
	padding: "0 18px",
	borderRadius: "14px",
	border: "1px solid #ddd6fe",
	background: "#f5f3ff",
	color: "#6d28d9",
	display: "inline-flex",
	alignItems: "center",
	gap: "8px",
	fontSize: "14px",
	fontWeight: 700,
	cursor: "pointer",
  },
  sectionsWrap: {
	display: "flex",
	flexDirection: "column",
	gap: "14px",
  },
  sectionCard: {
	background: "#fff",
	border: "1px solid #e5e7eb",
	borderRadius: "18px",
	padding: "16px",
  },
  sectionHeader: {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	gap: "16px",
	marginBottom: "14px",
  },
  sectionTitleWrap: {
	display: "flex",
	alignItems: "center",
	gap: "10px",
  },
  sectionIconBadge: {
	width: "26px",
	height: "26px",
	borderRadius: "8px",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
  },
  sectionTitle: {
	margin: 0,
	fontSize: "22px",
	fontWeight: 700,
	color: "#111827",
  },
  viewAllBtn: {
	display: "inline-flex",
	alignItems: "center",
	gap: "6px",
	background: "transparent",
	border: "none",
	color: "#4f46e5",
	fontSize: "14px",
	fontWeight: 700,
	cursor: "pointer",
  },
  serviceGrid: {
	display: "grid",
	gap: "14px",
  },
  serviceCard: {
	background: "#fff",
	border: "1px solid #ececec",
	borderRadius: "14px",
	overflow: "hidden",
	cursor: "pointer",
	transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },
  serviceImage: {
	width: "100%",
	aspectRatio: "1 / 1",
	objectFit: "cover",
	display: "block",
	background: "#f3f4f6",
  },
  serviceBody: {
	padding: "10px 12px",
  },
  serviceName: {
	margin: 0,
	fontSize: "14px",
	fontWeight: 700,
	color: "#111827",
	lineHeight: 1.4,
  },
  serviceSub: {
	margin: "6px 0 0",
	fontSize: "12px",
	color: "#6b7280",
	lineHeight: 1.5,
  },
  specialCard: {
	display: "grid",
	gridTemplateColumns: "160px 1fr",
	alignItems: "stretch",
  },
  specialImage: {
	height: "100%",
	minHeight: "120px",
  },
  specialBody: {
	padding: "14px",
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
  },
  emptyState: {
	background: "#fff",
	border: "1px solid #e5e7eb",
	borderRadius: "18px",
	padding: "48px 24px",
	textAlign: "center",
  },
  emptyTitle: {
	margin: "14px 0 8px",
	fontSize: "22px",
	fontWeight: 700,
	color: "#111827",
  },
  emptyText: {
	margin: 0,
	color: "#6b7280",
	fontSize: "15px",
  },
};

export default AllServicesPage;